import React, { useEffect, useState } from "react";
import {
	Typography,
	Card,
	CardContent,
	Box,
	Grid,
	Divider,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	TextField,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PeopleIcon from "@mui/icons-material/People";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useParams } from "react-router-dom"; // To get query params
import styles from "./roundOpening.module.css"; // Define styles here
import apiCall from "../../../API/apiService";
import apiEndpoints from "../../../API/apiEndpoints";
import Loader from "../../../../components/Loader";

const RoundOpening = () => {
	const [data, setData] = useState(null); // State to hold fetched data
	const [loading, setLoading] = useState(true);

	// Function to fetch data based on id
	const fetchData = async (id) => {
		try {
			const response = await apiCall("get", `${apiEndpoints.ROLE}/${id}`); // Assuming API call
			console.log(response);
			setData(response); // Save the fetched data to state
			setLoading(false)
		} catch (error) {
			setLoading(false)
			console.error("Error fetching data:", error);
		}
	};

	useEffect(() => {
		const url = window.location.href;
		const pathSegments = url.split("/");
		const id = pathSegments[4];
		console.log("id:", id);
		if (id) {
			fetchData(id);
		}
	}, []);

	const handleNumericInput = (e) => {
		e.target.value = e.target.value.replace(/\D/g, '');
	};

	const handleAlphabeticInput = (e) => {
		e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
	};

	if (loading) {
		return <Loader />;
	}

	return (
		<div className={styles.container}>
			<Card
				className={styles.card}
				sx={{
					mb: 3,
					backgroundColor: "#ffffff",
					borderRadius: 3,
					boxShadow: 3,
					padding: 3,
					margin: 2,
					width: '95%',
					maxWidth: '1300px',
					height: "600px",
					overflow: "hidden",
				}}
			>
				<CardContent>
					<Box
						sx={{
							width: '95%',
							maxWidth: '1300px',
							height: '500px',
							position: 'relative',
							borderRadius: '12px',
							backgroundColor: 'var(--White, rgba(255, 255, 255, 1))',
							boxShadow: `0px 1px 2px 1px rgba(0, 0, 0, 0.06),
									0px 1px 3px 1px rgba(0, 0, 0, 0.1),
									0px 0px 0px 1px rgba(17, 24, 39, 0.1)`,
							padding: '24px',
							boxSizing: 'border-box',
						}}
					>
						<Typography 
							variant="h5" 
							sx={{ 
								color: 'var(--Dark-Blue-700, rgba(29, 88, 216, 1))',
								fontFamily: 'Inter',
								fontWeight: 500, 
								fontSize: '18px', 
								lineHeight: '24px', 
								letterSpacing: '-0.3px', 
								textAlign: 'left', 
								textUnderlinePosition: 'from-font', 
								textDecorationSkipInk: 'none', 
								width: '253px', 
								height: '24px', 
								mb: 2
							}}
						>
							{data?.role_name}
						</Typography>

						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<div style={cellContentStyle}>
									<strong style={strongStyle}>Department:</strong>
									<span style={spanStyle}>{data?.department_name}</span>
								</div>

								<Divider style={{height: '30px', margin: '0 24px', borderLeft: '1px solid #ccc'}} />

								<div style={cellContentStyle}>
									<strong style={strongStyle}>Sub-Department:</strong>
									<span style={spanStyle}>{data?.subdepartment_name}</span>
								</div>
							</Box>
						</Box>

						<Divider sx={{ my: 2 }} />

						<Grid container spacing={3} sx={{ width: '100%', overflowX: 'auto', flexWrap: 'wrap' }}>
							{/* Work Arrangement */}
							<Grid item xs={12} sm={6} md={4} lg={3}>
								<div style={cellContentStyle}>
									<strong style={strongStyle}>Work Arrangement</strong>
									<span style={spanStyle}>{data?.work_arrangement || "N/A"}</span>
								</div>
							</Grid>

							{/* Level */}
							<Grid item xs={12} sm={6} md={4} lg={3}>
								<div style={cellContentStyle}>
									<strong style={strongStyle}>Level</strong>
									<span style={spanStyle}>{data?.level || "N/A"}</span>
								</div>
							</Grid>

							{/* Salary */}
							<Grid item xs={12} sm={6} md={4} lg={3}>
								<div style={cellContentStyle}>
									<strong style={strongStyle}>Salary</strong>
									<span style={spanStyle}>
										{data?.salary_min} - {data?.salary_max}
									</span>
								</div>
							</Grid>

							{/* Total Rounds */}
							<Grid item xs={12} sm={6} md={4} lg={3}>
								<div style={cellContentStyle}>
									<strong style={strongStyle}>Number of Rounds</strong>
									<span style={spanStyle}>{data?.total_rounds || "N/A"}</span>
								</div>
							</Grid>

							{/* City */}
							<Grid item xs={12} sm={6} md={4} lg={3}>
								<div style={cellContentStyle}>
									<strong style={strongStyle}>City</strong>
									<span style={spanStyle}>{data?.location_id || "N/A"}</span>
								</div>
							</Grid>
						</Grid>
					</Box>

					{/* Round details */}
					{data?.rounds?.length > 0 && (
						<Box mt={5}>
							<Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
								<Typography
									variant="h6"
									sx={{ 
										width: 'auto',
										height: '24px', 
										gap: '6px',
										fontFamily: 'Inter',
										fontSize: { xs: '14px', sm: '16px' },
										fontWeight: 500,
										lineHeight: '24px',
										letterSpacing: '-0.3px',
										textAlign: 'left',
										textUnderlinePosition: 'from-font',
										textDecorationSkipInk: 'none',
										color: 'var(--Gray-900, rgba(58, 58, 64, 1))',
									}}
								>
									Round Details: <span style={{ display: 'inline' }}>({data.rounds.length}/{data.total_rounds})</span>
								</Typography>
							</Box>

							{data.rounds.map((round, roundIndex) => (
								<React.Fragment key={roundIndex}>
									<Accordion
										sx={{
											mb: 2,
											backgroundColor: "#f7f9fc",
											borderRadius: 1,
											boxShadow: 1,
										}}
									>
										<AccordionSummary
											expandIcon={<ExpandMoreIcon />}
											sx={{ borderBottom: "1px solid #ddd" }}
										>
											<Typography
												variant="subtitle1"
												sx={{ fontWeight: "bold" }}
											>
												{round.round_name} ({round.type})
											</Typography>
										</AccordionSummary>
										<AccordionDetails>
											{Object.values(round?.question_bank).map(
												(question, qIndex) => (
													<Box key={qIndex} sx={{ pb: 1 }}>
														<Typography
															variant="body1"
															sx={{ fontWeight: "bold" }}
														>
															Q: {question.question}
														</Typography>
														<Typography
															variant="body2"
															sx={{ ml: 2, color: "#555" }}
														>
															A: {question.answer}
														</Typography>
													</Box>
												)
											)}
										</AccordionDetails>
									</Accordion>
								</React.Fragment>
							))}
						</Box>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

const cellContentStyle = {
	display: "flex",
	flexDirection: "column",
	gap: '4px',
	marginBottom: '12px'
};

const strongStyle = {
	minWidth: '109px',
	height: '18px',
	fontFamily: 'Inter',
	fontSize: '13px',
	fontWeight: 500,
	lineHeight: '18px',
	letterSpacing: '-0.2px',
	textAlign: 'left',
	color: 'var(--Gray-900, rgba(117, 117, 130, 1))',
	marginBottom: '10px'
};

const spanStyle = {
	minWidth: '150px',
	height: '22px',
	fontFamily: 'Inter',
	fontSize: '16px',
	fontWeight: 400,
	lineHeight: '24px',
	letterSpacing: '-0.2px',
	textAlign: 'left',
	textUnderlinePosition: 'from-font',
	textDecorationSkipInk: 'none',
	color: 'var(--Gray-900, rgba(58, 58, 64, 1))',
	whiteSpace: 'normal',
	wordBreak: 'break-word',
	marginBottom: '12px'
};

export default RoundOpening;
